import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {commonFetchListResponse, handleResponseError, ListResponse} from '../../../common/api-helpers';
import {destroy$} from '../../../components/destroyable';
import {PAGINATION_MAX_PAGE_SIZE} from '../../../components/table-pagination/pagination-constants';
import {QueryLangFilterOperator} from '../../../enums/query-lang-filter-operator';
import {SortDirection} from '../../../enums/sort-direction';
import {ListFilterFactory} from '../../../factories/list-filter.factory';
import {CommonListFilter} from '../../../models/filters/common-list-filter';
import {QueryLangFilterCriterion} from '../../../models/filters/query-lang-filter-criterion';
import {AppConfigService} from '../../../services/config.service';
import {TxHistoryRec, TxHistoryStatus} from '../tx-history-rec';
import {transactionApiFromTxHistoryRec, txHistoryRecFromTransactionApi} from './transaction-api.mapper';
import {TransactionApiModel} from './transaction-api.model';

@Injectable({providedIn: 'root'})
export class TransactionApiService {

    public readonly txAdded$: Observable<TxHistoryRec>;
    public readonly txUpdated$: Observable<TxHistoryRec>;

    public readonly SORT_COLUMN: keyof TransactionApiModel = 'createdAt';
    public readonly SORT_DIRECTION = SortDirection.DESC;

    private readonly destroy$ = destroy$();

    private readonly txAddedSubject$ = new Subject<TxHistoryRec>();
    private readonly txUpdatedSubject$ = new Subject<TxHistoryRec>();

    private readonly transactionApiUrl: string = this.appConfigService.api['transactions'];

    constructor(
        private httpClient: HttpClient,
        private appConfigService: AppConfigService
    ) {
        this.txAdded$ = this.txAddedSubject$.asObservable();
        this.txUpdated$ = this.txUpdatedSubject$.asObservable();
    }

    public getTransactions(listFilter?: CommonListFilter): Observable<ListResponse<TxHistoryRec>> {
        return this.getApiTransactions(listFilter).pipe(
            map(response => ({
                data: response.data.map(tx => txHistoryRecFromTransactionApi(tx)),
                listInfo: response.listInfo
            }))
            // TODO: Merge transactions from browser
        );
    }

    public getAllTransactionsByStatuses(statuses: TxHistoryStatus[]): Observable<TxHistoryRec[]> {
        const filters = [
            new QueryLangFilterCriterion<TransactionApiModel>({
                fieldNames: ['status'],
                operator:   QueryLangFilterOperator.IN,
                query:      statuses.join(',')
            })
        ];

        const listFilter = ListFilterFactory.createWithSearchFields(
            filters,
            this.SORT_COLUMN,
            this.SORT_DIRECTION,
            PAGINATION_MAX_PAGE_SIZE
        );

        return this.getTransactions(listFilter).pipe(
            map(response => response.data || [])
            // TODO: Merge transactions from browser
        );
    }

    public createTransaction(tx: TxHistoryRec): Observable<TxHistoryRec> {
        const apiTx = transactionApiFromTxHistoryRec(tx);

        return this.createApiTransaction(apiTx).pipe(
            map(() => tx),
            tap(tx => this.txAddedSubject$.next(tx))
        );
    }

    public updateTransaction(tx: TxHistoryRec): Observable<TxHistoryRec> {
        const apiTx = transactionApiFromTxHistoryRec(tx);

        return this.updateApiTransaction(apiTx).pipe(
            map(() => tx),
            tap(tx => this.txUpdatedSubject$.next(tx))
        );
    }

    public getTransactionByHash(hash: string): Observable<TxHistoryRec> {
        return this.getApiTransactionByHash(hash).pipe(
            map(tx => txHistoryRecFromTransactionApi(tx))
        );
    }

    private getApiTransactions(listFilter?: CommonListFilter): Observable<ListResponse<TransactionApiModel>> {
        return commonFetchListResponse(this.httpClient, this.transactionApiUrl, listFilter);
    }

    private getApiTransactionByHash(hash: string): Observable<TransactionApiModel> {
        const url = `${this.transactionApiUrl}/${hash}`;

        return this.httpClient.get<TransactionApiModel>(url).pipe(
            catchError(handleResponseError)
        );
    }

    private createApiTransaction(transactionData: Partial<TransactionApiModel>): Observable<TransactionApiModel> {
        return this.httpClient.post<TransactionApiModel>(this.transactionApiUrl, transactionData).pipe(
            catchError(handleResponseError)
        );
    }

    private updateApiTransaction(transactionData: Partial<TransactionApiModel>): Observable<TransactionApiModel> {
        return this.httpClient.post<TransactionApiModel>(this.transactionApiUrl, transactionData).pipe(
            catchError(handleResponseError)
        );
    }
}
